"use client";

import { useMemo } from "react";
import Link from "next/link";
import { FileDown, CheckCircle2 } from "lucide-react";
import { useUser } from "@clerk/nextjs";
import DashboardCard from "./DashboardCard";
import DashboardCardError from "./DashboardCardError";
import DashboardCardLoading from "./DashboardCardLoading";
import {
  getLatestPdfVersionInfo,
  hasPdfVersionAtLeast,
} from "@/components/PdfVersion";
import { PDF_PRODUCTS, getProductName } from "@/constants/pdf-products";
import { usePurchasesQuery } from "@/hooks/usePurchasesQuery";

const PDF_IDS = new Set<string>(PDF_PRODUCTS.map((p) => p.id));

/** "YYYY.MM.DD" → epoch ms (KST 자정 기준). releaseAt이 없는 과거 버전 fallback. */
function pdfDateMs(date: string): number {
  return new Date(date.replace(/\./g, "-") + "T00:00:00+09:00").getTime();
}

function latestReleaseMs(version: { date: string; releaseAt?: string }): number {
  return version.releaseAt ? Date.parse(version.releaseAt) : pdfDateMs(version.date);
}

type NeedItem = { productId: string; reason: "missing" | "outdated" };

/**
 * 대시보드 "요약 PDF 업데이트" 위젯 (JCG-2073).
 *
 * 구매한 요약 PDF가 최신 버전인지 알려준다. 새 다운로드부터 저장되는 `downloadedPdfVersions`
 * 누적 목록을 우선 비교하고, 버전 기록이 없는 과거 주문만 배포 시각(`releaseAt`) 대비 마지막
 * 다운로드 시각(`downloadedAt`)으로 fallback한다. 미다운로드도 알림. 클릭 시 구매 목록(다운로드)로.
 *
 * JCG-3854: Clerk 로딩·조회 중은 로딩, 성공한 빈 결과만 PDF 미보유, 조회 실패는 오류와 다시 시도로 구분한다.
 * 실패를 빈 구매로 두면 "구매한 요약 PDF가 없어요."로 오표시된다.
 */
export default function DashboardPdfUpdates({
  className,
}: {
  className?: string;
}) {
  const { isLoaded, user } = useUser();
  const { data, isPending, isError, refetch } = usePurchasesQuery();
  const purchases = data?.purchases;
  // 비로그인은 조회하지 않아 isPending이 유지되므로 빈 구매로 본다. /my는 게스트에게 이 위젯을 렌더하지 않는다.
  const loading = isPending && !(isLoaded && !user);

  const { ownedCount, needs } = useMemo(() => {
    // productId → 구매한 주문들의 다운로드 버전/시각 누적 정보.
    const byProduct = new Map<
      string,
      {
        downloadedVersions: string[];
        newestDownloadedAt: number | null;
        hasDownload: boolean;
      }
    >();
    for (const p of purchases ?? []) {
      if (!PDF_IDS.has(p.productId)) continue;
      const cur =
        byProduct.get(p.productId) ??
        { downloadedVersions: [], newestDownloadedAt: null, hasDownload: false };
      const downloadedVersions = [
        ...(Array.isArray(p.downloadedPdfVersions) ? p.downloadedPdfVersions : []),
        ...(p.downloadedPdfVersion ? [p.downloadedPdfVersion] : []),
      ];
      cur.downloadedVersions.push(...downloadedVersions);
      if (p.downloadedAt) {
        const downloadedAt = new Date(p.downloadedAt).getTime();
        cur.hasDownload = true;
        if (cur.newestDownloadedAt === null || downloadedAt > cur.newestDownloadedAt) {
          cur.newestDownloadedAt = downloadedAt;
        }
      }
      byProduct.set(p.productId, cur);
    }
    const result: NeedItem[] = [];
    for (const [productId, info] of byProduct) {
      if (!info.hasDownload) {
        result.push({ productId, reason: "missing" });
        continue;
      }
      const latest = getLatestPdfVersionInfo(productId);
      if (!latest) continue;
      if (hasPdfVersionAtLeast(info.downloadedVersions, latest.version)) {
        continue;
      }
      if (info.downloadedVersions.length === 0 && info.newestDownloadedAt !== null) {
        if (info.newestDownloadedAt >= latestReleaseMs(latest)) continue;
      }
      if (
        info.downloadedVersions.length > 0 ||
        info.newestDownloadedAt === null ||
        info.newestDownloadedAt < latestReleaseMs(latest)
      ) {
        result.push({ productId, reason: "outdated" });
      }
    }
    return { ownedCount: byProduct.size, needs: result };
  }, [purchases]);

  return (
    <DashboardCard
      icon={FileDown}
      title="요약 PDF"
      href="/my/purchases"
      linkLabel="구매 목록"
      className={className}
    >
      {loading ? (
        <DashboardCardLoading height={88} label="PDF 정보 불러오는 중…" />
      ) : isError && !data ? (
        <DashboardCardError
          message="PDF 정보를 불러오지 못했어요."
          onRetry={() => void refetch()}
        />
      ) : ownedCount === 0 ? (
        <p className="py-6 text-center text-[13px] text-muted-foreground">
          구매한 요약 PDF가 없어요.
        </p>
      ) : needs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-1.5 py-6 text-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-500" aria-hidden />
          <p className="text-[13px] text-muted-foreground">
            모든 PDF가 최신 버전이에요.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[13px] font-bold text-amber-600 dark:text-amber-400">
            새로 받을 PDF {needs.length}개
          </p>
          <ul className="space-y-1">
            {needs.slice(0, 2).map((n) => (
              <li key={n.productId}>
                <Link
                  href="/my/purchases"
                  className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] transition-colors hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700"
                >
                  <span className="min-w-0 truncate text-foreground">
                    {getProductName(n.productId)}
                  </span>
                  <span className="shrink-0 font-bold text-amber-600 dark:text-amber-400">
                    {n.reason === "missing" ? "미다운로드" : "새 버전"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {needs.length > 2 && (
            <p className="text-[11px] text-muted-foreground">
              외 {needs.length - 2}개 더
            </p>
          )}
        </div>
      )}
    </DashboardCard>
  );
}
